import React, { useState, useRef, useEffect } from 'react';
import { Play, Download, Heart, Clock, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../services/api';

const PackCard = ({ pack, onPreview, onDownload, onToggleFavorite }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [inView, setInView] = useState(false);
  const videoRef = useRef(null);
  const thumbVideoRef = useRef(null);
  const cardRef = useRef(null);

  const getThumbnailUrl = (url) => {
    if (!url) return '';
    if (url.includes('cloudinary.com')) {
      return url
        .replace('/upload/', '/upload/so_2,w_500,c_fill,g_auto,f_jpg/')
        .replace(/\.[^/.]+$/, '.jpg');
    }
    return url;
  };

  const thumbnailUrl = getThumbnailUrl(pack.thumbnail_url);
  const videoUrl = pack.video_url || '';
  const noThumb = !thumbnailUrl;
  const showVideoThumb = (noThumb || imageError) && videoUrl;

  const isHorizontal = pack.orientation === 'horizontal';
  const previewVideoUrl = videoUrl.includes('cloudinary.com')
    ? videoUrl.replace('/upload/', isHorizontal ? '/upload/q_auto:low,w_640,vc_h264/' : '/upload/q_auto:low,w_400,vc_h264/')
    : videoUrl;

  const isFavorite = pack.isFavorite || pack.is_favorite;
  const is4K = pack.is4K || pack.is_4k;

  // IntersectionObserver: carrega o <video> fallback só quando entra na viewport
  useEffect(() => {
    if (!showVideoThumb) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '100px' }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [showVideoThumb]);

  // Dispara play direto após o src ser definido no hover
  useEffect(() => {
    if (!isHovering || !videoRef.current) return;
    videoRef.current.play().catch(() => {});
  }, [isHovering]);

  const handleDownloadInternal = async (e) => {
    e.stopPropagation();
    if (typeof onDownload === 'function') {
      onDownload(pack);
      return;
    }
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      await api.downloadPack(pack.id);
    } catch (err) {
      console.error("Erro ao processar download:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group relative rounded-2xl overflow-hidden bg-[#0A0A12] border border-white/5 transition-all active:scale-[0.98] md:hover:border-[#0056D2]/40 shadow-lg cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => typeof onPreview === 'function' && onPreview(pack)}
    >
      {/* AREA DO VIDEO/THUMBNAIL */}
      <div
        ref={cardRef}
        className={`relative overflow-hidden bg-[#05050A] ${pack.orientation === 'horizontal' ? 'aspect-[16/9]' : 'aspect-[9/16]'}`}
      >
        {/* Skeleton enquanto carrega */}
        {!imageLoaded && !showVideoThumb && (
          <div className="absolute inset-0 bg-white/5 animate-pulse z-20" />
        )}

        {showVideoThumb ? (
          inView ? (
            /* Thumb via primeiro frame do vídeo (currentTime=0 funciona mesmo sem faststart) */
            <video
              ref={thumbVideoRef}
              src={videoUrl}
              className={cn(
                "absolute inset-0 w-full h-full object-cover transition-opacity duration-500 z-10",
                isHovering ? "opacity-0" : "opacity-100"
              )}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                e.target.currentTime = 0;
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-white/[0.04] z-10" />
          )
        ) : (
          <img
            src={thumbnailUrl}
            alt={pack.title}
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-500 z-10",
              imageLoaded ? "opacity-100" : "opacity-0",
              isHovering ? "opacity-0" : "opacity-100"
            )}
            onLoad={() => setImageLoaded(true)}
            onError={() => { setImageError(true); setImageLoaded(true); }}
          />
        )}

        {/* Vídeo de hover: src só é definido quando hovering (zero download em repouso) */}
        <video
          ref={videoRef}
          src={isHovering ? previewVideoUrl : undefined}
          poster={thumbnailUrl || undefined}
          loop
          muted
          playsInline
          preload="none"
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-300 z-0",
            isHovering ? "opacity-100" : "opacity-0"
          )}
        />

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent opacity-90 z-20" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-30">
          <div className="bg-[#0056D2] text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter shadow-lg">
            {is4K ? '4K ULTRA' : 'HD PRO'}
          </div>
        </div>

        {/* Botão Favorito */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onToggleFavorite === 'function') onToggleFavorite(pack.id);
          }}
          className={cn(
            "absolute top-3 right-3 p-2.5 rounded-xl transition-all z-30 backdrop-blur-md",
            isFavorite
              ? "bg-[#0056D2] text-white shadow-[#0056D2]/30 shadow-lg"
              : "bg-black/40 text-white/60 hover:text-white"
          )}
        >
          <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
        </button>

        {/* Ícone Play (Centro) */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center pointer-events-none z-20 transition-all duration-300",
            isHovering ? "opacity-0 scale-90" : "opacity-100 scale-100"
          )}
        >
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 shadow-2xl">
            <Play className="w-5 h-5 text-white ml-0.5 fill-white" />
          </div>
        </div>
      </div>

      {/* FOOTER DO CARD */}
      <div className="p-4 bg-[#0A0A12]">
        <h3 className="text-white text-[11px] font-bold truncate mb-3 uppercase italic tracking-tight font-['Helvetica',_sans-serif]">
          {pack.title}
        </h3>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-slate-500">
            <Clock className="w-3 h-3" />
            <span className="text-[10px] font-bold tracking-tighter">
              {pack.duration?.toFixed(1)}S
            </span>
          </div>

          {pack.storage_account === 'C' ? (
            <span className="text-[9px] text-[#0056D2] font-bold uppercase tracking-widest opacity-60">
              Referência
            </span>
          ) : (
            <button
              onClick={handleDownloadInternal}
              disabled={isDownloading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-[#0056D2] border border-white/10 hover:border-[#0056D2] rounded-xl text-white text-[10px] font-bold transition-all active:scale-95 disabled:opacity-50 group/btn"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0056D2]" />
              ) : (
                <Download className="w-3.5 h-3.5 group-hover/btn:animate-bounce" />
              )}
              <span className="uppercase tracking-widest">
                {isDownloading ? '...' : 'Baixar'}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackCard;
